import React, { useState } from "react";
import DatabaseMatch from "./DatabaseMatch";
import "./UserCard.css";

/**
 * UserCard component displays information about a pending user request
 *
 * @param {Object} props
 * @param {Object} props.pendingUser - User data including GroupMe info and potential database matches
 * @param {Object} props.selectedUserId - ID of currently selected user for this groupme request
 * @param {Object} props.editableUser - User currently being edited
 * @param {Object} props.editedFields - Fields that have been edited
 * @param {boolean} props.isSaving - Whether a save operation is in progress
 * @param {Object} props.saveMessage - Message about save operation
 * @param {Function} props.onSelectUser - Function to call when selecting a user for approval
 * @param {Function} props.onToggleEdit - Function to call to toggle edit mode
 * @param {Function} props.onFieldChange - Function to call when a field changes
 * @param {Function} props.onSaveUser - Function to call to save user data
 * @param {Function} props.onStartMerge - Function to call to start the merge process
 * @param {Function} props.onLoadMatches - Function to call to load database matches for this user
 * @returns {JSX.Element}
 */
const UserCard = ({
  pendingUser,
  selectedUserId,
  editableUser,
  editedFields,
  isSaving,
  saveMessage,
  onSelectUser,
  onToggleEdit,
  onFieldChange,
  onSaveUser,
  onStartMerge,
  onLoadMatches,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);

  // Check if matches have been loaded (matchCount of -1 means not loaded yet)
  const matchesNotLoaded = pendingUser.matchCount === -1;

  // Check if user has multiple matches (potential conflict)
  const hasConflict =
    pendingUser.matchingDbUsers && pendingUser.matchingDbUsers.length > 1;
  const matchCount = pendingUser.matchingDbUsers
    ? pendingUser.matchingDbUsers.length
    : 0;

  // Handle expand/collapse and load matches if needed
  const handleToggleExpand = async () => {
    if (!isExpanded && matchesNotLoaded && onLoadMatches) {
      setIsLoadingMatches(true);
      await onLoadMatches(pendingUser);
      setIsLoadingMatches(false);
    }
    setIsExpanded(!isExpanded);
  };

  // Check if this user is currently being edited
  const isEditing =
    editableUser &&
    pendingUser.matchingDbUsers &&
    pendingUser.matchingDbUsers.some(
      (dbUser) => dbUser.userId === editableUser.userId
    );

  // Function to copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        // Could add a toast notification here
        console.log("Copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  // Get first name for template messages
  const firstName = pendingUser.groupmeData.nickname.split(" ")[0];

  // Format the timestamp to human-readable date/time
  const formatRequestDate = (timestamp) => {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp * 1000); // Convert seconds to milliseconds

    // Format as "MMM DD, YYYY at HH:MM AM/PM"
    const options = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };

    return date.toLocaleString('en-US', options);
  };

  const requestDate = formatRequestDate(pendingUser.groupmeData.timestamp);

  // Template messages
  const welcomeMessage = `Hey ${firstName}! It's David from Active Beaches! I just added you to the group chat. Please let me know if you can see it!`;
  const followUpMessage = `Awesome! Please let me know if you can't find the events or if anything in the chat is unclear.

Hope to meet you soon! Stoked to have you in the group 🤙`;
  const groupMeLinkMessage = `Hey ${firstName}, this is David from Active Beaches. Just wanted to see if you were able to get the link to join the group chat on the Groupme app or if you needed me to send it to you.`;

  return (
    <div
      className={`user-card ${hasConflict ? "conflict" : ""} ${
        isEditing ? "editing" : ""
      }`}
    >
      {isEditing && <div className="editing-badge">Editing User</div>}
      <div className="user-header">
        <div className="user-avatar">
          {pendingUser.groupmeData.image_url ? (
            <img
              src={pendingUser.groupmeData.image_url}
              alt={pendingUser.groupmeData.nickname}
            />
          ) : (
            <div className="placeholder-avatar">
              {pendingUser.groupmeData.nickname.charAt(0)}
            </div>
          )}
        </div>
        <div className="user-info">
          <h3>{pendingUser.groupmeData.nickname}</h3>
          <p>Phone: {pendingUser.groupmeData.phone}</p>
          <p className="request-date">Requested: {requestDate}</p>
          <button
            className="expand-btn"
            onClick={handleToggleExpand}
            disabled={isLoadingMatches}
          >
            {isLoadingMatches
              ? "Loading..."
              : isExpanded
              ? "Hide Details"
              : matchesNotLoaded
              ? "Show Details & Load Matches"
              : `Show Details (${matchCount} match${matchCount !== 1 ? "es" : ""})`}
          </button>

          {hasConflict && (
            <div className="conflict-badge">
              Multiple Matches ({matchCount})
            </div>
          )}
        </div>
        {hasConflict && matchCount >= 2 && (
          <div className="user-merge-action">
            <button
              className="merge-btn"
              onClick={() => onStartMerge(pendingUser)}
            >
              Merge Records
            </button>
          </div>
        )}
      </div>

      {/* Show details when expanded */}
      {isExpanded && (
        <>
          {/* Template Messages with Copy Buttons */}
          <div className="template-messages">
            <div className="template-message">
              <p className="message-text">{welcomeMessage}</p>
              <button
                className="copy-btn"
                onClick={() => copyToClipboard(welcomeMessage)}
                title="Copy welcome message"
              >
                Copy
              </button>
            </div>
            <div className="template-message">
              <p className="message-text">{followUpMessage}</p>
              <button
                className="copy-btn"
                onClick={() => copyToClipboard(followUpMessage)}
                title="Copy follow-up message"
              >
                Copy
              </button>
            </div>
            <div className="template-message">
              <p className="message-text">{groupMeLinkMessage}</p>
              <button
                className="copy-btn"
                onClick={() => copyToClipboard(groupMeLinkMessage)}
                title="Copy GroupMe-link check-in message"
              >
                Copy
              </button>
            </div>
          </div>

          <div className="matches-container">
            <h4>Database Matches</h4>
            {isLoadingMatches ? (
              <div className="loading-matches">Loading database matches...</div>
            ) : !pendingUser.matchingDbUsers ||
              pendingUser.matchingDbUsers.length === 0 ? (
              <div className="no-matches">No matches found in database</div>
            ) : (
              <>
              {/* #215: fuzzy candidates are returned only when there
                  was NO exact match — flag them loudly so a close-but-
                  different number is a conscious pairing decision. */}
              {pendingUser.matchingDbUsers.some((u) => u.fuzzyMatch) && (
                <div
                  style={{
                    background: "#fdf7e3",
                    border: "1px solid #f0d47a",
                    borderRadius: 8,
                    padding: "8px 12px",
                    marginBottom: 8,
                    fontSize: 13,
                  }}
                >
                  ≈ No exact phone match — showing <strong>close</strong>{" "}
                  numbers (1–2 digits off, e.g. 994- for 904-). Compare
                  the phone on each card before pairing.
                </div>
              )}
              {pendingUser.matchingDbUsers.map((dbUser) => (
                <DatabaseMatch
                  key={dbUser.userId}
                  dbUser={dbUser}
                  isSelected={selectedUserId === dbUser.userId}
                  isEditable={editableUser && editableUser.userId === dbUser.userId}
                  editedFields={editedFields}
                  isSaving={isSaving}
                  saveMessage={saveMessage}
                  groupmeId={pendingUser.groupmeData.id}
                  onSelect={() =>
                    onSelectUser(
                      pendingUser.groupmeData.id,
                      pendingUser.groupmeData.user_id,
                      dbUser.userId
                    )
                  }
                  onEdit={() => onToggleEdit(dbUser)}
                  onFieldChange={onFieldChange}
                  onSave={() =>
                    onSaveUser(pendingUser.groupmeData.id, dbUser.userId)
                  }
                />
              ))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserCard;
